import { EMPTY_STRING } from '../../../NonReact/constants/Common'
import { DEFAULT_CME_ID } from '../../AddUser/Constants'
import { ALL_VALUE } from '../../Common/API/ItemsGetters'
import { isBitmaskMatching } from '../../Common/Utils/BitwiseOperations'
import { ANY_NUMBER_OF_TELSYS_AVAILABLE, ANY_NUMBER_OF_TELSYS_AVAILABLE_VALUE, CapabilityBitFlags } from '../Constants'
import {
    CMEBranding,
    CMEBrandingBackend,
    CMEBrandingBackendEntry,
    CMEBrandingEntry,
    CMEBrandingRepeatableEntry,
    CMEBrandingSubItemBackendEntry,
    CMEBrandingSubItemEntry,
    CMEDetailsData,
    CMEFormHookData,
    ServicePackItem,
    ServicePackItemForm
} from '../Types'
import { DATA_URL_PREFIX, dataUriToFile } from '../../Common/Utils/FileUtils'
import { APIData } from '../../../NonReact/common/Types/Types'

export const processBranding = (branding: CMEBrandingBackend): CMEBranding =>
    Object.fromEntries(
        Object.entries(branding).map(([key, value]: CMEBrandingBackendEntry): CMEBrandingEntry => {
            if (typeof value === 'boolean') {
                return [key, value]
            }

            if (Array.isArray(value)) {
                return [
                    key,
                    value.map(
                        (entry: CMEBrandingRepeatableEntry): CMEBrandingRepeatableEntry =>
                            Object.fromEntries(
                                Object.entries(entry).map(
                                    ([key2, value2]: CMEBrandingSubItemEntry): CMEBrandingSubItemEntry => [
                                        key2,
                                        typeof value2 === 'string' && value2.startsWith(DATA_URL_PREFIX)
                                            ? dataUriToFile(value2, key2)
                                            : value2
                                    ]
                                )
                            )
                    )
                ]
            }

            return [
                key,
                Object.fromEntries(
                    Object.entries(value).map(
                        ([key1, value1]: CMEBrandingSubItemBackendEntry): CMEBrandingSubItemEntry => {
                            if (typeof value1 === 'string' && value1.startsWith(DATA_URL_PREFIX)) {
                                return [key1, dataUriToFile(value1, key1)]
                            }

                            return [key1, value1]
                        }
                    )
                )
            ]
        })
    )

/**
 * CME tab data mapper.
 *
 * @param response The data from JSON response.
 */
const CMELoadDataMapper = ({ Data }: APIData<CMEDetailsData>): CMEFormHookData => ({
    details: {
        description: Data.Description ?? EMPTY_STRING,
        id: Data.CMEnvironmentID ?? EMPTY_STRING,
        billingid: Data.BillingTag ?? EMPTY_STRING,
        parentcme: Data.ParentCMEnvironmentID ?? DEFAULT_CME_ID,
        providerids: Data?.IdentityProviderIDs ?? EMPTY_STRING,
        disableAkixiLogin: Data?.DisableAkixiLogin ?? false
    },
    domainProfile: {
        domain: Data.Domain ?? EMPTY_STRING,
        serveripaddress: Data.ServerIPAddress ?? EMPTY_STRING,
        tcpport: parseInt(Data.ServerTCPPort, 10) ?? 0,
        communicationactive: Data.CommunicationActivated ?? false,
        fromaddressemail: Data.FromAddressEmail ?? EMPTY_STRING,
        fromaddressname: Data.FromAddressName ?? EMPTY_STRING,
        companyname: Data.CompanyName ?? EMPTY_STRING,
        teamsappdownloadurl: Data.AppDownloadURL ?? EMPTY_STRING,
        cxiClientWindowsUrl: Data.CXIClientDownloadURLWin ?? EMPTY_STRING,
        cxiClientMacUrl: Data.CXIClientDownloadURLMac ?? EMPTY_STRING,
        searchAndCallUrl: Data.SearchAndCallAppDownloadURL ?? EMPTY_STRING,
        useauthentication: Data.UseAuthentication ?? false,
        username: Data.AuthUsername ?? EMPTY_STRING,
        password: Data.AuthPassword ?? EMPTY_STRING,
        cxiAccessCode: Data.CXIAccessCode ?? EMPTY_STRING
    },
    provisioning: {
        broadworks: {
            servicepacks:
                Data.ProvisioningConfig?.BroadworksProvisioning?.ServicePacks?.map(
                    (servicePackItem: ServicePackItem): ServicePackItemForm => ({
                        ...servicePackItem,
                        TelSysIDs: servicePackItem.TelSysIDs.split(',').map((id: string) => parseInt(id, 10))
                    })
                ) ?? []
        }
    },
    licenseManagement: {
        EssentialsCap:
            Data?.LicenseCapManagement?.EssentialsCap === ALL_VALUE ||
            Data?.LicenseCapManagement?.EssentialsCap === null
                ? EMPTY_STRING
                : `${Data?.LicenseCapManagement?.EssentialsCap}`,
        ProfessionalCap:
            Data?.LicenseCapManagement?.ProfessionalCap === ALL_VALUE ||
            Data?.LicenseCapManagement?.ProfessionalCap === null
                ? EMPTY_STRING
                : `${Data?.LicenseCapManagement?.ProfessionalCap}`,
        EnterpriseCap:
            Data?.LicenseCapManagement?.EnterpriseCap === ALL_VALUE ||
            Data?.LicenseCapManagement?.EnterpriseCap === null
                ? EMPTY_STRING
                : `${Data?.LicenseCapManagement?.EnterpriseCap}`,
        CallCentreAgentCap:
            Data?.LicenseCapManagement?.CallCentreAgentCap === ALL_VALUE ||
            Data?.LicenseCapManagement?.CallCentreAgentCap === null
                ? EMPTY_STRING
                : `${Data?.LicenseCapManagement?.CallCentreAgentCap}`,
        CallCentreSupervisorCap:
            Data?.LicenseCapManagement?.CallCentreSupervisorCap === ALL_VALUE ||
            Data?.LicenseCapManagement?.CallCentreSupervisorCap === null
                ? EMPTY_STRING
                : `${Data?.LicenseCapManagement?.CallCentreSupervisorCap}`,
        CXIntegrationCap:
            Data?.LicenseCapManagement?.CXIntegrationCap === ALL_VALUE ||
            Data?.LicenseCapManagement?.CXIntegrationCap === null
                ? EMPTY_STRING
                : `${Data?.LicenseCapManagement?.CXIntegrationCap}`
    },
    capabilities: {
        maxTelsysCount:
            (Data.MaxTelsysCount ?? ANY_NUMBER_OF_TELSYS_AVAILABLE_VALUE) !== ANY_NUMBER_OF_TELSYS_AVAILABLE_VALUE
                ? (Data.MaxTelsysCount ?? ANY_NUMBER_OF_TELSYS_AVAILABLE).toString()
                : ANY_NUMBER_OF_TELSYS_AVAILABLE,
        cxaBroadworks: isBitmaskMatching(Data.Capabilities, CapabilityBitFlags.CXA_BROADWORKS),
        cxaWebex: isBitmaskMatching(Data.Capabilities, CapabilityBitFlags.CXA_WEBEX),
        cxaTeams: isBitmaskMatching(Data.Capabilities, CapabilityBitFlags.CXA_TEAMS),
        cxiBroadworks: isBitmaskMatching(Data.Capabilities, CapabilityBitFlags.CXI_BROADWORKS),
        cxiWebex: isBitmaskMatching(Data.Capabilities, CapabilityBitFlags.CXI_WEBEX),
        cxiTeams: isBitmaskMatching(Data.Capabilities, CapabilityBitFlags.CXI_TEAMS),
        legacyTelephony: isBitmaskMatching(Data.Capabilities, CapabilityBitFlags.LEGACY_TELEPHONY),
        legacy: isBitmaskMatching(Data.Capabilities, CapabilityBitFlags.CXA_LEGACY),
        enterprise: isBitmaskMatching(Data.Capabilities, CapabilityBitFlags.ENTERPRISE),
        showUiVersionToggle: Data?.ShowUiVersionToggle ?? false
    },
    branding: processBranding(Data?.BrandingConfig ?? {})
})

export default CMELoadDataMapper
